import {
    getCameraPlaneNormals,
    fitPointsIntoView,
    makePlaneNormals,
} from "../../Q3/camera/thumbnail-view-all/fit-points.js";

const CheckNanProperties = [
    ["position", "xyz"],
    ["quaternion", "xyzw"],
    ["scale", "xyz"],
];

const EDGES_MULTIPLIER = 1.25;
const DEBUG_BOUNDING_CYLINDER = false;
const DEBUG_FIT_POINTS = false;
let helperMesh = null;
let helperPoints = null;

function meshBoundingBoxValid(mesh) {
    if (mesh.isMesh && mesh.visible && mesh.geometry && !isGeometryEmpty(mesh.geometry)) {
        for (const [name, keys] of CheckNanProperties) {
            const property = mesh[name];

            for (const key of keys) {
                if (isNaN(property[key])) {
                    return false;
                }
            }
        }

        return true;
    }

    return false;
}

function getSceneBoundingBox() {
    const sceneBoundingBox = new THREE.Box3();
    sceneBoundingBox.makeEmpty();

    Q3.RenderManager.activeScene.updateMatrixWorld(true);

    Q3.RenderManager.activeScene.traverseVisible(object => {
        const mesh = Q3.assets.objects[object.uuid];

        if (mesh === object && meshBoundingBoxValid(mesh))
            sceneBoundingBox.expandByObject(mesh);
    });

    if (sceneBoundingBox.min.x === Infinity) return;

    return sceneBoundingBox;
}

function* iterateObjectVertices(object, pos) {
    const mat = object.matrixWorld;
    const posArray = object.geometry.getAttribute("position").array;

    for (let i = 0; i < posArray.length; i += 3) {
        yield pos.set(posArray[i+0], posArray[i+1], posArray[i+2]).applyMatrix4(mat);
    }
}

function getSceneBoundingCylinder() {
    const center = getSceneBoundingBox().getCenter(new THREE.Vector3());
    const { x: cx, y: cy, z: cz } = center;
    let sqRadius = 0, halfHeight = 0;

    const pos = new THREE.Vector3();

    Q3.RenderManager.activeScene.traverseVisible(object => {
        const mesh = Q3.assets.objects[object.uuid];

        if (mesh === object && meshBoundingBoxValid(mesh)) {
            for (const { x, y, z } of iterateObjectVertices(object, pos)) {
                const dx = x-cx, dy = y-cy, dz = z-cz;
                sqRadius = Math.max(sqRadius, dx*dx+dz*dz);
                halfHeight = Math.max(halfHeight, Math.abs(dy));
            }
        }
    });

    return {
        center,
        radius: Math.sqrt(sqRadius),
        height: halfHeight*2,
    };
}

function isGeometryEmpty(geometry) {
    const pos = geometry.getAttribute('position');
    return !pos || pos.count === 0;
}

function getCameraPlacementFromBoundingSphere(sceneBoundingSphere) {
    const newCamDist = sceneBoundingSphere.radius * 3 / Math.tan(Q3.RenderManager.activeCamera.fov * THREE.Math.DEG2RAD);//3 : zooms out to more than the bounding sphere's radius

    const targetToCam = Q3.controls.target.clone().sub(Q3.RenderManager.activeCamera.position).setLength(newCamDist);
    const nextCamPos = sceneBoundingSphere.center.clone().sub(targetToCam);

    return {
        camX: nextCamPos.x,
        camY: nextCamPos.y,
        camZ: nextCamPos.z,
        tarX: sceneBoundingSphere.center.x,
        tarY: sceneBoundingSphere.center.y,
        tarZ: sceneBoundingSphere.center.z,
    };
}

function getCameraPlacement() {
    const sceneBoundingBox = getSceneBoundingBox();
    const sceneBoundingSphere = sceneBoundingBox.getBoundingSphere(new THREE.Sphere());
    return getCameraPlacementFromBoundingSphere(sceneBoundingSphere)
}

function getBoundingCylinder(boundingBox) {
    const {
        min: { x: x0, y: y0, z: z0 },
        max: { x: x1, y: y1, z: z1 },
    } = boundingBox;

    const sx = (x1-x0)*0.5, sz = (z1-z0)*0.5;

    return {
        center: new THREE.Vector3(x0+x1, y0+y1, z0+z1).multiplyScalar(0.5),
        radius: Math.sqrt(sx*sx+sz*sz),
        height: y1-y0,
    };
}

const fitPlaneNormals = makePlaneNormals();

function* iterateCylinderPoints(cylinder) {
    const position = new THREE.Vector3();
    const { center, radius, height } = cylinder;

    for (let i = 0; i < 16; i++) {
        const angle = i*Math.PI*2/16

        const x = center.x+Math.sin(angle)*radius;
        const z = center.z+Math.cos(angle)*radius;

        for (let j = 0; j < 2; j++) {
            const y = center.y+height*(j-0.5);
            yield position.set(x, y, z);
        }
    }
}

function getCameraPlacementFromBoundingCylinder(cylinder) {
    /*
     * fitPointsIntoView is inaccurate for some shapes so normally there would have
     * to be corrections after the call (like in view-all-points.js in data event
     * based viewer, where camera was showing up at wrong height for asset
     * 641414e897ef77741a4bcdcd), but it should be fine for cylinder as it is.
     */
    const position = new THREE.Vector3();
    getCameraPlaneNormals(Q3.RenderManager.activeCamera, EDGES_MULTIPLIER, fitPlaneNormals);
    const points = iterateCylinderPoints(cylinder);
    fitPointsIntoView(points, fitPlaneNormals, position);

    if (DEBUG_FIT_POINTS) {
        if (!helperPoints) {
            const geo = new THREE.BufferGeometry();
            const posAttrib = new THREE.BufferAttribute(new Float32Array(96), 3, false);
            geo.addAttribute("position", posAttrib);
            const mat = new THREE.PointsMaterial({ color: 0xffff00, size: 2, sizeAttenuation: false });
            helperPoints = new THREE.Points(geo, mat);
            Q3.RenderManager.activeScene.add(helperPoints);
        }

        const posAttrib = helperPoints.geometry.getAttribute("position");
        let i = 0;
        for (const { x, y, z } of iterateCylinderPoints(cylinder)) {
            posAttrib.array[i++] = x;
            posAttrib.array[i++] = y;
            posAttrib.array[i++] = z;
        }
        posAttrib.needsUpdate = true;
    }

    const lookDir = Q3.controls.target.clone().sub(Q3.RenderManager.activeCamera.position).normalize();
    const distance = lookDir.dot(cylinder.center)-lookDir.dot(position);
    const target = lookDir.clone().multiplyScalar(distance).add(position);

    return {
        camX: position.x, camY: position.y, camZ: position.z,
        tarX: target.x, tarY: target.y, tarZ: target.z,
    };
}

function getThumbnailCameraPlacement() {
    const sceneBoundingCylinder = getSceneBoundingCylinder();

    if (DEBUG_BOUNDING_CYLINDER) {
        if (!helperMesh) {
            const geo = new THREE.CylinderBufferGeometry(1, 1, 1, 32, 1);
            const mat = new THREE.MeshBasicMaterial({ color: 0xffff00, wireframe: true });
            helperMesh = new THREE.Mesh(geo, mat);
            Q3.RenderManager.activeScene.add(helperMesh);
        }

        const { center, radius, height } = sceneBoundingCylinder;
        helperMesh.position.copy(center);
        helperMesh.scale.set(radius, height, radius);
    }

    return getCameraPlacementFromBoundingCylinder(sceneBoundingCylinder);
}

const tmpBox3 = new THREE.Box3(), tmpSphere = new THREE.Sphere();
function viewAll(isInitial) {
    console.log("viewAll");
    Q3.CameraTweenTrack.finishTweens();

    Q3.RenderManager.waitSceneBoundingBox(tmpBox3).then((sceneBoundingBox) => {
        const sceneBoundingSphere = sceneBoundingBox.getBoundingSphere(tmpSphere);

        if (sceneBoundingSphere.radius < 1e-5 || !isFinite(sceneBoundingSphere.radius)) {
            sceneBoundingSphere.radius = 1;
            sceneBoundingSphere.center.set(0, 0, 0);
        }

        const camEnd = getCameraPlacementFromBoundingSphere(sceneBoundingSphere)
        Q3.RenderManager.activeSceneBoundingBox = sceneBoundingBox;

        const tween = {
            camX: Q3.RenderManager.activeCamera.position.x,
            camY: Q3.RenderManager.activeCamera.position.y,
            camZ: Q3.RenderManager.activeCamera.position.z,
            tarX: Q3.controls.target.x,
            tarY: Q3.controls.target.y,
            tarZ: Q3.controls.target.z
        };

        Q3.controls.enabled = false;

        Q3.RenderManager.activeCamera.activeTween = TweenLite.to(tween, 0.25, {
            camX: camEnd.camX,
            camY: camEnd.camY,
            camZ: camEnd.camZ,
            tarX: camEnd.tarX,
            tarY: camEnd.tarY,
            tarZ: camEnd.tarZ,
            onUpdate() {
                Q3.RenderManager.activeCamera.position.set(tween.camX, tween.camY, tween.camZ);
                Q3.RenderManager.activeCamera.lookAt(tween.tarX, tween.tarY, tween.tarZ);

                Q3.controls.target.set(tween.tarX, tween.tarY, tween.tarZ);
                Q3.controls.update();

                Q3.RenderManager.activeCamera.update = true;
            },

            async onComplete() {
                if (isInitial && Q3.isAssetMode) {
                    Q3.controls.update();
                    Q3.RenderManager.activeCamera.update = true;
                    await Q3.RenderManager.enqueRaycasterAndAwait();

                    Q3.labels.live.createMissingLabels();

                    const controls = document.getElementById("asset-viewer-label-controls");

                    if (controls) controls.classList.add("active");
                    Q3.controls.enabled = true;
                }

                Q3.SlideManager.saveSlideView(Q3.SlideManager.activeSlide, {
                    target: new THREE.Vector3(camEnd.tarX, camEnd.tarY, camEnd.tarZ),
                    position: new THREE.Vector3(camEnd.camX, camEnd.camY, camEnd.camZ)
                });
            }
        });
    });
}

export { viewAll, getCameraPlacement, getThumbnailCameraPlacement };
export default viewAll;